import "@progress/kendo-ui/js/kendo.datepicker";
import "@progress/kendo-ui/js/kendo.upload";
import * as autosize from "autosize";
import * as ko from "knockout";
import * as _ from "lodash";
import * as moment from "moment";
import * as AppointmentData from "../data/appointmentData";
import { ITargetMaster } from "../data/targetData";
import { IApplication } from "../interfaces/application";
import { ICodeModel } from "../interfaces/data";
import * as Data from "../interfaces/data";
import { ITranslatedText } from "../interfaces/translations";
import { ICustomerSettings } from "../interfaces/user";
import * as Constants from "./constants";
import { PHRASE_GENERALACTIONFAILURE } from "./constants";
import * as Global from "./global";
import * as UI from "./ui";
import * as Util from "./util";

// options used when creating this control
export interface IOptions {
    // function to call when an additional form should be opened
    openAdditionalForm?: (formItemIndex: number, hasExceptionForm: boolean, templateFormID: number, field?: IFieldObservable) => void;
}

// data needed to load this control with form data
export interface ILoadParams {
    FormItems: AppointmentData.IAppointmentFormItem[];
    LanguageID: number;
}

// definition of the form item object
interface IFormItemObservable {
    ID: KnockoutObservable<number>;
    FormText: KnockoutComputed<string>;
    FormTextDefault: KnockoutObservable<string>;
    FormTextTranslations: ITranslatedText[];
    selected: KnockoutObservable<string>;
    ItemHasValue: KnockoutObservable<boolean>;
    selectedArray: KnockoutObservableArray<string>;
    AppointmentID: KnockoutObservable<number>;
    ControlTypeID: KnockoutObservable<number>;
    isSelected: KnockoutObservable<boolean>;
    SignatureUploaded: KnockoutObservable<boolean>;
    IsRequired: KnockoutObservable<boolean>;
    IsReadOnly: KnockoutObservable<boolean>;
    IsVisible: KnockoutObservable<boolean>;
    Fields: AppointmentData.IAppointmentField[];
    FormAnswer: KnockoutObservableArray<AppointmentData.IAppointmentFormAnswer>;
    Comments: KnockoutObservableArray<AppointmentData.IAppointmentComment>;
    Documents: KnockoutObservableArray<AppointmentData.IAppointmentDocument>;
    TemplatePhrases: KnockoutObservableArray<AppointmentData.ITemplatePhraseDetail>;
    AdditionalTemplateFormID: KnockoutObservable<number>;
    TemplateFormItemID: KnockoutObservable<number>;
    dropdownselected: KnockoutObservable<string>;
    AdditionalFormItems: KnockoutObservableArray<AppointmentData.IAppointmentAdditionalFormItem>;
    AppointmentFormItemDocumentTypesAllowed: KnockoutObservableArray<AppointmentData.IAppointmentFormItemDocumentTypeAllowed>;
    AppointmentFormItemLinkedCustomerDocuments: KnockoutObservableArray<AppointmentData.IAppointmentFormItemLinkedCustomerDocument>;
    MultipleAdditionalForms: KnockoutObservable<boolean>;
    AvailableAdditionalForm: KnockoutComputed<number>;
    TargetOptions: KnockoutComputed<ICodeModel<number>>;
    SelectedTargetValues: KnockoutComputed<Array<ICodeModel<string>>>;
    NumberOfDecimals: KnockoutObservable<number>;
    MaskValue: KnockoutObservable<string>;
    MaxVal: KnockoutObservable<string>;
    MinVal: KnockoutObservable<string>;
    MinLength: KnockoutObservable<number>;
    MaxLength: KnockoutObservable<number>;
    AppointmentControlTypeAttribute: KnockoutObservable<AppointmentData.IAppointmentControlTypeAttribute>;

    // Geo location control related properties and methods
    Latitude: KnockoutObservable<number>;
    Longitude: KnockoutObservable<number>;
    ValidLat: KnockoutObservable<boolean>;
    ValidLong: KnockoutObservable<boolean>;
    LatLongMissing: KnockoutObservable<boolean>;
    SingleCheckboxSelected: KnockoutObservable<boolean>;
}

// definition of the template phrase detail object
interface ITemplatePhraseDetail {
    ID: KnockoutObservable<number>;
    Phrase: KnockoutObservable<string>;
    SortOrder: KnockoutObservable<number>;
}

// definition of the field object
export interface IFieldObservable {
    ID: KnockoutObservable<number>;
    AppointmentFormItemID: KnockoutObservable<number>;
    FieldText: KnockoutObservable<string>;
    FieldValue: KnockoutObservable<string>;
    SortOrder: KnockoutObservable<number>;
    IsActive: KnockoutObservable<boolean>;
    HasException: KnockoutObservable<boolean>;
    HasExceptionForm: KnockoutObservable<boolean>;
    TemplateFormID: KnockoutObservable<number | null>;
}

// definition for the view model
interface IViewModel {
    FormItems: KnockoutObservableArray<IFormItemObservable>;
    DateFormat: string;
    TimeFormat: string;
    allowedFileTypes: KnockoutObservable<string>;
    uploadMaxFileSizeWarning: KnockoutObservable<string>;
    uploadedFormItemIndex: KnockoutObservable<number>;
    uploadedCommentIndex: KnockoutObservable<number>;
    uploadedDocumentIndex: KnockoutObservable<number>;
    uploadedGuid: KnockoutObservable<string>;
    uploadSignature: KnockoutObservable<boolean>;
    PictureURL: KnockoutObservable<string>;
    CustomerDocumentDownloadUrl: string;
    LanguageID: KnockoutObservable<number>;
    errors?: KnockoutValidationErrors;

    closeUploadModal(): void;
    closeUploadDocumentModal(): void;
    closePictureModal(): void;
}

export interface IFormItemsMaster {
    FormItems: AppointmentData.IAppointmentFormItem[];
    errors: KnockoutValidationErrors;
}

interface IGeolocation {
    Latitude: number;
    Longitude: number;
    Speed: number;
    Altitude: number;
    Bearing: number;
    RadiusOfUncertainty: number;
    RecordedDateTime: string;
}
interface ITargetForControl {
    ID: number;
    Name: string;
}

// a control for display a form and letting the user edit
// the values in the form
export class FormDataEntryControl {

    // -------------------------------------------------------------------------
    // class properties
    // -------------------------------------------------------------------------
    private container: JQuery;
    private documentUploadControl: kendo.ui.Upload;
    private viewModel: IViewModel;
    private fieldsOnLoad: string;
    private options: IOptions;
    private customerSettings: ICustomerSettings[];
    // -------------------------------------------------------------------------
    // constructor
    // -------------------------------------------------------------------------
    constructor(options: IOptions) {
        this.options = options;
        const app: IApplication = Global.app();
        const me: FormDataEntryControl = this;
        this.customerSettings = app.userSession ? app.userSession.CustomerSettings : [];
        this.viewModel = {
            FormItems: ko.observableArray<IFormItemObservable>(),
            allowedFileTypes: ko.observable<string>(),
            uploadMaxFileSizeWarning: ko.observable<string>(),
            uploadedFormItemIndex: ko.observable<number>(),
            uploadedCommentIndex: ko.observable<number>(),
            uploadedDocumentIndex: ko.observable<number>(),
            uploadedGuid: ko.observable<string>(""),
            uploadSignature: ko.observable<boolean>(),
            PictureURL: ko.observable<string>(),

            DateFormat: UI.GetCustomerSetting(this.customerSettings, Constants.SETTING_KEY_DATEFORMAT, Constants.DEFAULT_DATE_FORMAT),
            TimeFormat: UI.GetCustomerSetting(this.customerSettings, Constants.SETTING_KEY_TIMEFORMAT, Constants.DEFAULT_TIME_FORMAT),
            closeUploadModal(): void {
                me.container.find("div[data-id='uploadDialog']").modal("hide");
            },
            closeUploadDocumentModal(): void {
                me.container.find("div[data-id='uploadDocumentDialog']").modal("hide");
            },
            closePictureModal(): void {
                me.container.find("div[data-id='pictureFormDialog']").modal("hide");
                me.viewModel.PictureURL("");
            },

            CustomerDocumentDownloadUrl: app.webServiceBaseURL + "/api/DocumentLibrary/Download/",
            LanguageID: ko.observable<number>(),
        };

        this.viewModel.errors = ko.validation.group(this.viewModel.FormItems, { deep: true });
    }

    // -------------------------------------------------------------------------
    // public methods
    // -------------------------------------------------------------------------
    public render(container: JQuery): JQueryPromise<any> {
        const me: FormDataEntryControl = this;
        const app: IApplication = Global.app();
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();

        this.container = container;
        container.hide();

        app.templates().render("formdataentrycontrol", "template_formdataentrycontrol", this.container)
            .done(() => {
                // initialize kendo upload control
                me.container.find("input[data-id='uploadControl']").kendoUpload({
                    async: {
                        saveUrl: "save",
                        removeUrl: "remove",
                        autoUpload: true,
                    },
                    validation: {
                        allowedExtensions: [".jpg", ".jpeg", ".png"], // TODO - read extensions from DocumentFileTypeAllowed table
                    },
                    upload: me.onUpload.bind(me),
                    complete: me.onComplete.bind(me),
                    success: me.onSuccess.bind(me),
                    multiple: false,
                });
                // initialize kendo upload control for documents
                // note that any "validation" properties are set in the "createFormItemObservable" function
                // because they can be different for each field.
                me.documentUploadControl = me.container.find("input[data-id='uploadDocumentControl']").kendoUpload({
                    async: {
                        saveUrl: "save",
                        removeUrl: "remove",
                        autoUpload: true,
                    },
                    upload: me.onUpload.bind(me),
                    complete: me.onCompleteDocument.bind(me),
                    success: me.onSuccess.bind(me),
                    multiple: false,
                }).data("kendoUpload");

                autosize(me.container.find("textarea"));

                ko.applyBindings(me.viewModel, me.container[0]);

                deferred.resolve();
            });

        return deferred.promise();
    }

    // display the form fields
    public load(loadParams: ILoadParams): void {
        const me: FormDataEntryControl = this;
        this.viewModel.FormItems.removeAll();
        this.ConvertDateTimeFormatsForFormItems(loadParams.FormItems);
        loadParams.FormItems.forEach((formItem: AppointmentData.IAppointmentFormItem) => {
            me.viewModel.FormItems.push(me.createFormItemObservable(formItem));
        });
        // remember initial value so we can determine if any data has changed
        this.fieldsOnLoad = ko.toJSON(this.getFormItems());
        me.viewModel.errors!.showAllMessages(false);
        me.viewModel.uploadSignature(false);
        me.viewModel.LanguageID(loadParams.LanguageID);
    }

    public changeLocation(formItemViewModel: IFormItemObservable, position: Position): void {
        const me: FormDataEntryControl = this;
        formItemViewModel.Latitude(position.coords.latitude);
        formItemViewModel.Longitude(position.coords.longitude);
        formItemViewModel.ItemHasValue(true);
        formItemViewModel.selected(JSON.stringify(me.getLocationObject( "", formItemViewModel.Latitude(), formItemViewModel.Longitude())));
    }
    public getLocationObject(formItemAnswer: string, latitude: number , longitude: number): IGeolocation {
        const existingGeoLoc: IGeolocation = formItemAnswer ? JSON.parse(formItemAnswer) : null;
        if ( existingGeoLoc != null && existingGeoLoc.Latitude === latitude && existingGeoLoc.Longitude === longitude) {
            return existingGeoLoc;
        } else {
            const locationObject: IGeolocation = {
                Latitude: latitude,
                Longitude: longitude,
                Speed : 0,
                Altitude : 0,
                Bearing : 0,
                RadiusOfUncertainty : 0,
                RecordedDateTime: moment().utc().format(),
            };
            return locationObject;
        }
    }

    // returns the values entered by the user
    public getFormItems(): IFormItemsMaster {
        const formItems: AppointmentData.IAppointmentFormItem[] = [];

        this.viewModel.FormItems().forEach((formItem: IFormItemObservable) => {
            const newFormItem: AppointmentData.IAppointmentFormItem = {
                ID: formItem.ID(),
                AdditionalTemplateFormID: formItem.AdditionalTemplateFormID(),
                TemplateFormItemID: formItem.TemplateFormItemID(),
                AppointmentID: formItem.AppointmentID(),
                FormAnswer: formItem.FormAnswer(),
                ControlTypeID: formItem.ControlTypeID(),
                selected: formItem.ControlTypeID() === Constants.QuestionType.GEOLOCAITON ? this.currentValues(formItem) : formItem.selected(),
                selectedArray: formItem.selectedArray(),
                dropdownselected: formItem.dropdownselected(),
                Comments: formItem.Comments(),
                Documents: formItem.Documents(),
                IsRequired: formItem.IsRequired(),
                IsReadOnly: formItem.IsReadOnly(),
                IsVisible: formItem.IsVisible(),
                AdditionalFormItems: formItem.AdditionalFormItems(),
                AppointmentFormItemDocumentTypesAllowed: formItem.AppointmentFormItemDocumentTypesAllowed(),
                MultipleAdditionalForms: formItem.MultipleAdditionalForms(),
                Targets: [],
            };
            formItems.push(newFormItem);
        });

        const saveFormItems: any = {
            FormItems: formItems,
            errors: this.viewModel.errors,
        };
        return saveFormItems;
    }

    // validating the values entered by user
    public isValid(): boolean {
        let isValid: boolean = true;

        // checking for knockout validation for selected fields(textboxes,checkboxes,radio buttons,drop down controls)
        if (this.viewModel.errors && this.viewModel.errors().length > 0) {
            this.viewModel.errors.showAllMessages(true);
            isValid = false;
        }

        // validating the document, comment fields  and GeoLocation(latitude and longitude), if they are required
        this.viewModel.FormItems().forEach((formItem: IFormItemObservable) => {
            formItem.ItemHasValue(this.FormItemHasValue(formItem));

            // Setting the LatLongMissing flag
            formItem.LatLongMissing(this.LatLongMissing(formItem));

            isValid = formItem.ItemHasValue() ? isValid : false;
            isValid = !formItem.LatLongMissing() ? isValid : false;

            // we need to save only when Lat and Long are valid.
            isValid = formItem.ValidLat() && formItem.ValidLong() ? isValid : false;
       });

        return isValid;
    }

    // returns the values entered by the user
    public getFormItemsForSave(): AppointmentData.IAppointmentFormItem[] {
        const me: FormDataEntryControl = this;
        const formItems: AppointmentData.IAppointmentFormItem[] = [];

        this.viewModel.FormItems().forEach((formItem: IFormItemObservable) => {
            let selectedValue = formItem.selected();
            if (selectedValue) {
                // Convert Date/Time values from customer formats into standard formats
                const timeFormat: string = UI.getMomentTimeFormat(me.viewModel.TimeFormat);
                const dateFormat: string = UI.getMomentDateFormat(me.viewModel.DateFormat);

                switch (formItem.ControlTypeID()) {
                    case Constants.QuestionType.TIME:
                        selectedValue = moment(selectedValue, timeFormat).format(Constants.MOMENT_FORMAT_TIME_STANDARDIZED);
                        break;
                    case Constants.QuestionType.DATE:
                        selectedValue = moment(selectedValue, dateFormat).format(Constants.MOMENT_FORMAT_DATE_STANDARDIZED);
                        break;
                    case Constants.QuestionType.DATETIME:
                        selectedValue = moment(selectedValue, `${dateFormat} ${timeFormat}`).utc()
                                            .format(`${Constants.MOMENT_FORMAT_DATE_STANDARDIZED}[T]${Constants.MOMENT_FORMAT_TIME_STANDARDIZED}[Z]`);
                        break;
                    }
            }

            if (formItem.ControlTypeID() === Constants.QuestionType.GEOLOCAITON) {
                selectedValue = this.currentValues(formItem);
            }

            const newFormItem: AppointmentData.IAppointmentFormItem = {
                ID: formItem.ID(),
                TemplateFormItemID: formItem.TemplateFormItemID(),
                AppointmentID: formItem.AppointmentID(),
                FormAnswer: formItem.FormAnswer(),
                ControlTypeID: formItem.ControlTypeID(),
                selected: selectedValue,
                selectedArray: formItem.selectedArray(),
                dropdownselected: formItem.dropdownselected(),
                Comments: formItem.Comments(),
                Documents: formItem.Documents(),
                IsRequired: formItem.IsRequired(),
                IsReadOnly: formItem.IsReadOnly(),
                IsVisible: formItem.IsVisible(),
                AdditionalFormItems: formItem.AdditionalFormItems(),
                AppointmentFormItemDocumentTypesAllowed: formItem.AppointmentFormItemDocumentTypesAllowed(),
                MultipleAdditionalForms: formItem.MultipleAdditionalForms(),
                Targets: [],
            };
            formItems.push(newFormItem);
        });

        return ko.toJS(formItems);
    }

    public show(): void {
        this.container.show();
    }

    public hide(): void {
        this.container.hide();
    }

    public haveFieldsChanged(): boolean {
        const fieldValues: string = ko.toJSON(this.getFormItems());
        if (this.fieldsOnLoad === undefined) {
            return false;
        } else {
            return fieldValues !== this.fieldsOnLoad;
        }
    }

    public currentValues(formItem: IFormItemObservable): string {
        let selectedValue: string = "";
        if (this.latLongAreNumbers(formItem) && this.latLongAreNotNull(formItem)) {
            const lat: number =  Number(formItem.Latitude());
            const long: number = Number(formItem.Longitude());

            selectedValue = JSON.stringify(this.getLocationObject(formItem.selected(), lat, long));
        }
        return selectedValue;
    }

    public latLongAreNumbers(formItem: IFormItemObservable): boolean {
        return (!_.isNaN(formItem.Latitude()) && !_.isNaN(formItem.Longitude())) ? true : false;
    }
    public latLongAreNotNull(formItem: IFormItemObservable): boolean {
        return (!_.isNil(formItem.Latitude()) && !_.isNil(formItem.Longitude())) ? true : false;
    }

    // -------------------------------------------------------------------------
    // private methods
    // -------------------------------------------------------------------------

    // TODO - this function should return type IFormItemObservable, but the interface and how
    // its used do not match.  Need to fix the interface to match the usage.
    // https://alm.cgifederal.com/projects/browse/CUE-1482
    private createFormItemObservable(formItem: AppointmentData.IAppointmentFormItem): any {
        const me: FormDataEntryControl = this;
        const app: IApplication = Global.app();

        const url: string = app.webServiceBaseURL;
        if (formItem.IsAdditionalForm === undefined) {
            formItem.IsAdditionalForm = false;
        }
        const targetsForControl = this.getTargetListForControl(formItem.Targets);
        let controlTypeAttributes: AppointmentData.IAppointmentControlTypeAttribute | undefined = formItem.AppointmentControlTypeAttribute;
        if (!controlTypeAttributes) {
            controlTypeAttributes = {
                ID: 0,
                MinVal: "",
                MaxVal: "",
                Format: "",
                MinLength: 0,
                MaxLength: 0,
                Mask: "",
                NumDecimals: 0,
                NumericDefaultValue: 0,
                Interval: 1,
            };
        }

        // TODO - this should have type IFormItemObservable
        const formItemViewModel: any = {
            ID: ko.observable(formItem ? formItem.ID : 0),
            TemplateFormID: ko.observable(0), // TODO - is this needed?
            TemplateFormItemID: ko.observable(formItem ? formItem.TemplateFormItemID : 0),
            AppointmentID: ko.observable(formItem ? formItem.AppointmentID : 0),
            ControlTypeID: ko.observable(formItem ? formItem.ControlTypeID : 0),
            FormText: ko.pureComputed(() => {
                // Find the translation for the currently selected language
                if (formItemViewModel.FormTextTranslations) {
                    const translations = formItemViewModel.FormTextTranslations
                            .filter((formTextTranslations: any) => formTextTranslations.LanguageID === me.viewModel.LanguageID());
                    if (translations && translations.length > 0 && translations[0].TranslatedText) {
                        return translations[0].TranslatedText;
                    } else {
                        return formItemViewModel.FormTextDefault();
                    }
                } else {
                    return formItemViewModel.FormTextDefault();
                }
            }),
            FormTextDefault: ko.observable(formItem ? formItem.FormText : ""),
            FormTextTranslations: formItem.FormTextTranslations,
            IsRequired: ko.observable(formItem ? formItem.IsRequired : false),
            SortOrder: ko.observable(formItem ? formItem.SortOrder : 0),
            IsReadOnly: ko.observable(formItem ? formItem.IsReadOnly : false),
            IsVisible: ko.observable(formItem ? formItem.IsVisible : true),
            IsActive: ko.observable(true), // TODO - is this needed?
            ItemHasValue: ko.observable(true),
            ValidLatLong: ko.observable(true),
            AdditionalTemplateFormID: ko.observable(formItem ? formItem.AdditionalTemplateFormID : 0),
            Fields: ko.observableArray(),
            AppointmentControlTypeAttribute: ko.observable<AppointmentData.IAppointmentControlTypeAttribute>(controlTypeAttributes),
            AppointmentFormItemDocumentTypesAllowed: ko.observableArray(),
            AdditionalFormItems: ko.observableArray(),
            FormAnswer: ko.observableArray(),
            Comments: ko.observableArray(),
            Documents: ko.observableArray(),
            TemplatePhrases: ko.observableArray(),
            url: ko.observable(url),
            IsAdditionalForm: ko.observable(formItem ? formItem.IsAdditionalForm : false),
            TemplatePhraseID: ko.observable(formItem ? formItem.TemplatePhraseID : 0),
            SelectedPhrase: ko.observable(""),
            SelectedCommentPhrase: ko.observable(""),
            newFieldText: ko.observable(""),
            MaskValue: ko.observable<string>(controlTypeAttributes.Mask),
            MaxVal: ko.observable<string>(controlTypeAttributes.MaxVal),
            MinVal: ko.observable<string>(controlTypeAttributes.MinVal),
            MinLength: ko.observable<number>(controlTypeAttributes.MinLength),
            MaxLength: ko.observable<number>(controlTypeAttributes.MaxLength),
            NumberOfDecimals: ko.observable<number>(controlTypeAttributes.NumDecimals),
            NumericDefaultValue: ko.observable<number>(controlTypeAttributes.NumericDefaultValue),
            Interval: ko.observable<number>(controlTypeAttributes.Interval),
            PictureURL: ko.observable(""),
            isSelected: ko.observable(false),
            SignatureUploaded: ko.observable(false),
            displayTemplateName: ko.observable(me.GetTemplateName(formItem && formItem.ControlTypeID ? formItem.ControlTypeID : 1)),
            AppointmentFormItemLinkedCustomerDocuments: ko.observableArray(formItem.AppointmentFormItemLinkedCustomerDocuments),
            MultipleAdditionalForms: ko.observable(formItem.MultipleAdditionalForms),
            AvailableAdditionalForm: ko.observable(0),
            Latitude: ko.observable<number | null>(null),
            Longitude: ko.observable<number | null>(null),
            ValidLat: ko.observable(true),
            ValidLong: ko.observable(true),
            LatLongMissing: ko.observable(false),
            SingleCheckboxSelected: ko.observable(formItem.FormAnswer ? this.answerChecked(formItem.FormAnswer[0].Value) : false),
            buttonFormItemClick: (e: any) => {
                formItemViewModel.showAdditionalForm(e);
            },
            SelectedTargetValues: ko.pureComputed(() => {
                const selectedTargetID = formItemViewModel.selected().toString();
                if (!Util.isNullOrEmpty(formItem.Targets)) {
                    for (const target of formItem.Targets) {
                        if (target.ID.toString() === selectedTargetID) {
                            return me.getTargetValues(target);
                        }
                    }
                }
                return null;
            }),
            TargetOptions:  ko.pureComputed(() => {
                return targetsForControl;
            }),
        };

        formItemViewModel.selected = ko.observable(formItem.FormAnswer ? formItem.FormAnswer[0].Value : "").extend({
            required: {
                message: "This field is required.",
                params: true,
                onlyIf(): boolean {
                    return (!(formItem && formItem.IsReadOnly ? true : false)
                        && (formItem && formItem.IsRequired ? true : false)
                        && (formItem && formItem.IsVisible ? true : false)
                        && (formItem.ControlTypeID === Constants.QuestionType.TEXTAREA
                            || formItem.ControlTypeID === Constants.QuestionType.TEXTBOX
                            || formItem.ControlTypeID === Constants.QuestionType.EMAIL
                            || formItem.ControlTypeID === Constants.QuestionType.NUMERIC
                            || formItem.ControlTypeID === Constants.QuestionType.DATE
                            || formItem.ControlTypeID === Constants.QuestionType.TIME
                            || formItem.ControlTypeID === Constants.QuestionType.DATETIME
                            || formItem.ControlTypeID === Constants.QuestionType.BARCODE
                            || formItem.ControlTypeID === Constants.QuestionType.SIGNATURE
                            || formItem.ControlTypeID === Constants.QuestionType.PHONE
                            || formItem.ControlTypeID === Constants.QuestionType.RADIOBUTTONLIST
                            || formItem.ControlTypeID === Constants.QuestionType.TARGET
                            || formItem.ControlTypeID === Constants.QuestionType.ADDRESS
                            || formItem.ControlTypeID === Constants.QuestionType.SLIDER));
                },
            },
            maxLength: {
                onlyIf(): boolean {
                    if (formItem.AppointmentControlTypeAttribute == null) {
                        return false;
                    } else {
                        if (formItem.ControlTypeID === Constants.QuestionType.TEXTBOX &&
                            formItem.AppointmentControlTypeAttribute.MaxLength > 0) {
                            return true;
                        } else if (formItem.ControlTypeID === Constants.QuestionType.TEXTAREA
                            && formItem.AppointmentControlTypeAttribute.MaxLength > 0) {
                            return true;
                        } else if (formItem.ControlTypeID === Constants.QuestionType.BARCODE &&
                            formItem.AppointmentControlTypeAttribute.MaxLength > 0) {
                            return true;
                        } else if (formItem.ControlTypeID === Constants.QuestionType.ADDRESS &&
                            formItem.AppointmentControlTypeAttribute.MaxLength > 0) {
                            return true;
                        } else {
                            return false;
                        }
                    }
                },
                params: formItemViewModel.MaxLength(),
            },
            minLength: {
                onlyIf(): boolean {
                    if (formItem.AppointmentControlTypeAttribute == null) {
                        return false;
                    } else {
                        if (formItem.ControlTypeID === Constants.QuestionType.TEXTBOX &&
                            formItem.AppointmentControlTypeAttribute.MinLength > 0) {
                            return true;
                        } else if (formItem.ControlTypeID === Constants.QuestionType.TEXTAREA
                            && formItem.AppointmentControlTypeAttribute.MinLength > 0) {
                            return true;
                        } else if (formItem.ControlTypeID === Constants.QuestionType.BARCODE
                            && formItem.AppointmentControlTypeAttribute.MinLength > 0) {
                            return true;
                        } else if (formItem.ControlTypeID === Constants.QuestionType.ADDRESS
                            && formItem.AppointmentControlTypeAttribute.MinLength > 0) {
                            return true;
                        } else {
                            return false;
                        }
                    }
                },
                params: formItemViewModel.MinLength(),
            },
            max: {
                onlyIf(): boolean {
                    if (formItemViewModel.MaxVal() === "") {
                        return false;
                    } else if (formItem.AppointmentControlTypeAttribute == null) {
                        return false;
                    } else {
                        if (formItem.ControlTypeID === Constants.QuestionType.NUMERIC &&
                            !Util.isNullOrEmpty(formItem.AppointmentControlTypeAttribute.MaxVal)) {
                            return true;
                        } else {
                            return false;
                        }
                    }
                },
                params: formItemViewModel.MaxVal(),
            },
            min: {
                onlyIf(): boolean {
                    if (formItemViewModel.MinVal() === "") {
                        return false;
                    } else if (formItem.AppointmentControlTypeAttribute == null) {
                        return false;
                    } else {
                        if (formItem.ControlTypeID === Constants.QuestionType.NUMERIC &&
                            !Util.isNullOrEmpty(formItem.AppointmentControlTypeAttribute.MinVal)) {
                            return true;
                        } else {
                            return false;
                        }
                    }
                },
                params: formItemViewModel.MinVal(),
            },
            email: {
                onlyIf(): boolean {
                    if (formItem.ControlTypeID === Constants.QuestionType.EMAIL) {
                        return true;
                    } else {
                        return false;
                    }
                },
            },
            dateValid: {
                onlyIf(): boolean {
                    if (formItem.ControlTypeID === Constants.QuestionType.DATE) {
                        return true;
                    } else {
                        return false;
                    }
                },
            },
            timeValid: {
                onlyIf(): boolean {
                    if (formItem.ControlTypeID === Constants.QuestionType.TIME) {
                        return true;
                    } else {
                        return false;
                    }
                },
            },
            dateTimeValid: {
                onlyIf(): boolean {
                    if (formItem.ControlTypeID === Constants.QuestionType.DATETIME) {
                        return true;
                    } else {
                        return false;
                    }
                },
            },

        });

        // Parsing the string to JSON object to get the Latitude and Longitude values
        if (formItemViewModel.ControlTypeID() === Constants.QuestionType.GEOLOCAITON && formItemViewModel.selected()) {
              const geoLocation = JSON.parse(formItemViewModel.selected());
              if (geoLocation) {
                formItemViewModel.Latitude(geoLocation.Latitude);
                formItemViewModel.Longitude(geoLocation.Longitude);
              }
        }

        // dropdown selected value
        formItemViewModel.dropdownselected = ko.observable(formItem.FormAnswer ? formItem.FormAnswer[0].Value : "").extend({
            required: {
                message: "This field is required.",
                params: true,
                onlyIf(): boolean {
                    return (!(formItem && formItem.IsReadOnly ? true : false)
                        && formItem && formItem.IsRequired ? true : false
                        && formItem && formItem.IsVisible ? true : false)
                        && formItem.ControlTypeID === Constants.QuestionType.DROPDOWN;
                },
            },
        });

        formItemViewModel.removeField = (comment: any) => {
            formItemViewModel.Comments.remove(comment);
            formItemViewModel.ItemHasValue(me.FormItemHasValue(formItemViewModel));
        };
        formItemViewModel.addField = (newfield: any): any => {
            const comment: any = {
                ID: 0,
                Comment: newfield.newFieldText(),
            };
            formItemViewModel.Comments.push(me.Comments(comment));
            formItemViewModel.ItemHasValue(me.FormItemHasValue(formItemViewModel));
            newfield.newFieldText("");
        };

        formItemViewModel.currentLocation = () => {
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition( positionSuccess, positionNotFound);
            } else {
                updateItemHasValueFlag();
            }
            function positionSuccess(position: Position): void {
                // If the viewmodel already lat and lng , display confirmation message for use whether they need to change that.
                if (formItemViewModel.Latitude() !== null && formItemViewModel.Longitude() !== null) {
                    UI.showConfirmDialog("Are you sure you want to change the location?",
                    () => {
                       me.changeLocation(formItemViewModel, position);
                    });
                } else {
                // if the viewmodel doesn't have Lat and Lng , but current location btn clicked then update with current location.
                    me.changeLocation(formItemViewModel, position);
                }
                  }
            function positionNotFound(): void {
                UI.showErrorMessage(["The location could be not obtained from the browser. You may need to enable geolocation permissions for this web site."]);
            }
            function updateItemHasValueFlag(): void {
               if (!formItemViewModel.IsReadOnly() && formItemViewModel.IsRequired() && formItemViewModel.IsVisible()) {
                   // check lat and lng has values, if so then make the field true else false
                   formItemViewModel.ItemHasValue( _.isNaN(formItemViewModel.Latitude()) || _.isNaN(formItemViewModel.Longitude()) ?
                                                    false : true);
               } else {
                   formItemViewModel.ItemHasValue(true);
               }

            }
        };

        formItemViewModel.addDocument = (formItemIndex: any): any => {
            me.viewModel.uploadedFormItemIndex(formItemIndex());
            const allowedExtensions = formItemViewModel.AppointmentFormItemDocumentTypesAllowed().map((x: any) => x.DocumentTypeExtension());
            me.viewModel.allowedFileTypes(allowedExtensions.join(", "));
            const options: any = {
                validation: {
                    allowedExtensions,
                },
            };
            const maxFileSizeInBytes = Global.app().global.MaximumUploadFileSizeInBytes;
            if (maxFileSizeInBytes > 0) {
                options.validation.maxFileSize = maxFileSizeInBytes;
            }
            me.viewModel.uploadMaxFileSizeWarning(this.getMaxUploadFileSizeMessage(maxFileSizeInBytes));
            me.documentUploadControl.setOptions(options);
            me.container.find("div[data-id='uploadDocumentDialog']").modal("show");
            me.container.find(".k-upload-status-total").remove();
        };
        // checkbox list array of multiple selections
        formItemViewModel.selectedArray = ko.observableArray().extend({
            required: {
                message: "This field is required.",
                params: true,
                onlyIf(): boolean {
                    return (formItem && formItem.IsRequired ? true : false) && formItem.ControlTypeID === Constants.QuestionType.CHECKBOXLIST;
                },
            },
        });

        // subscribe to changes in dropdown to display additional form if field has excpetion form = true
        formItemViewModel.dropdownselected.subscribe((data: any, context: any) => {

            if (!data) { return; }

            for (const item of me.viewModel.FormItems()) {
                item.isSelected(false);
            }

            const selectedField: IFieldObservable = ko.utils.arrayFirst(formItemViewModel.Fields(),
                (item: IFieldObservable): boolean => {
                    return item.FieldValue() === data;
                });
            formItemViewModel.selected(data);

            let additionalFormID = formItemViewModel.AdditionalTemplateFormID();
            if (formItemViewModel.MultipleAdditionalForms()) {
                additionalFormID = selectedField.TemplateFormID() ? selectedField.TemplateFormID() : 0;
            }

            if (selectedField != null && selectedField.HasExceptionForm() || (formItemViewModel.MultipleAdditionalForms() && additionalFormID > 0)) {
                formItemViewModel.isSelected(true);
                const index: number = me.viewModel.FormItems().indexOf(formItemViewModel);
                if (me.options.openAdditionalForm) {
                    me.options.openAdditionalForm(index, true, additionalFormID);
                }
            }
        });

        formItemViewModel.SelectedPhrase.subscribe((phrase: string) => {
            if (phrase != null) {
                const text: string = formItemViewModel.selected() + phrase;
                formItemViewModel.selected(text);
            }
        });

        formItemViewModel.SelectedCommentPhrase.subscribe((phrase: string) => {
            if (phrase != null) {
                const text: string = formItemViewModel.newFieldText() + phrase;
                formItemViewModel.newFieldText(text);
            }
        });
        formItemViewModel.showAddSignatureButton = ko.pureComputed((): boolean => {
            return formItemViewModel.selected() === "";
        });

        formItemViewModel.typeIsTextbox = ko.pureComputed((): boolean => {
            return formItemViewModel.ControlTypeID() === Constants.QuestionType.TEXTBOX;
        });

        formItemViewModel.typeIsNumeric = ko.pureComputed((): boolean => {
            return formItemViewModel.ControlTypeID() === Constants.QuestionType.NUMERIC;
        });

        formItemViewModel.typeIsTextArea = ko.pureComputed((): boolean => {
            return formItemViewModel.ControlTypeID() === Constants.QuestionType.TEXTAREA;
        });

        formItemViewModel.typeIsDate = ko.pureComputed((): boolean => {
            return formItemViewModel.ControlTypeID() === Constants.QuestionType.DATE;
        });

        formItemViewModel.typeIsDateTime = ko.pureComputed((): boolean => {
            return formItemViewModel.ControlTypeID() === Constants.QuestionType.DATETIME;
        });

        formItemViewModel.typeIsTime = ko.pureComputed((): boolean => {
            return formItemViewModel.ControlTypeID() === Constants.QuestionType.TIME;
        });

        formItemViewModel.typeIsRadio = ko.pureComputed((): boolean => {
            return formItemViewModel.ControlTypeID() === Constants.QuestionType.RADIOBUTTONLIST;
        });

        formItemViewModel.typeIsDropdown = ko.pureComputed((): boolean => {
            return formItemViewModel.ControlTypeID() === Constants.QuestionType.DROPDOWN;
        });

        formItemViewModel.typeIsCheckbox = ko.pureComputed((): boolean => {
            return formItemViewModel.ControlTypeID() === Constants.QuestionType.CHECKBOXLIST;
        });

        formItemViewModel.typeIsSection = ko.pureComputed((): boolean => {
            return formItemViewModel.ControlTypeID() === Constants.QuestionType.SECTION;
        });

        formItemViewModel.typeIsButton = ko.pureComputed((): boolean => {
            return formItemViewModel.ControlTypeID() === Constants.QuestionType.BUTTON;
        });

        formItemViewModel.typeIsTextDesc = ko.pureComputed((): boolean => {
            return formItemViewModel.ControlTypeID() === Constants.QuestionType.TEXTDESCRIPTION;
        });

        formItemViewModel.typeIsBarcode = ko.pureComputed((): boolean => {
            return formItemViewModel.ControlTypeID === Constants.QuestionType.BARCODE;
        });

        formItemViewModel.typeIsDocuments = ko.pureComputed((): boolean => {
            return formItemViewModel.ControlTypeID === Constants.QuestionType.DOCUMENTS;
        });

        formItemViewModel.typeIsSingleCheckbox = ko.pureComputed((): boolean => {
            return formItemViewModel.ControlTypeID() === Constants.QuestionType.SINGLECHECKBOX;
       });

        $(formItem.FormAnswer).each((index: number, formAnswers: any) => {
            formItemViewModel.FormAnswer.push(me.FormAnswers(formAnswers));
            formItemViewModel.selectedArray.push(formAnswers.Value);
        });
        $(formItem.Fields).each((index: number, fields: any) => {
            formItemViewModel.Fields.push(me.Fields(fields));
        });

        $(formItem.AppointmentFormItemDocumentTypesAllowed).each((index: number, appointmentFormItemDocumentTypesAllowed: any) => {
            formItemViewModel.AppointmentFormItemDocumentTypesAllowed.push(me.FormItemDocumentFileTypeAllowed(appointmentFormItemDocumentTypesAllowed));
        });

        $(formItem.Comments).each((index: number, comments: any) => {
            formItemViewModel.Comments.push(me.Comments(comments));
        });

        $(formItem.Documents).each((index: number, documents: any) => {
            formItemViewModel.Documents.push(me.Documents(documents));
        });

        $(formItem.TemplatePhrases).each((index: number, phrases: any) => {
            formItemViewModel.TemplatePhrases.push(me.TemplatePhrases(phrases));
        });

        $(formItem.AdditionalFormItems).each((index: number, additionalformitems: any) => {
            formItemViewModel.AdditionalFormItems.push(me.AdditionalFormItems(additionalformitems));
        });

        formItemViewModel.showAdditionalForm = (formitem: any) => {
            if (me.options.openAdditionalForm && formitem.AvailableAdditionalForm()) {

                for (const item of me.viewModel.FormItems()) {
                    item.isSelected(false);
                }
                formitem.isSelected(true);

                const index: number = me.viewModel.FormItems().indexOf(formitem);
                me.options.openAdditionalForm(index, false, formitem.AvailableAdditionalForm());
            }
        };

        formItemViewModel.deleteSignature = (data: any, parent: any) => {
            // signature
            if (formItemViewModel.IsReadOnly()) {
                UI.showErrorMessage([Constants.PHRASE_CANNOTDELETEREADONLYIMAGE]);
            } else {
                me.deletePicture("Signature", formItemViewModel.FormAnswer()[0].ID()).then(() => {
                    formItemViewModel.selected("");
                    formItemViewModel.FormAnswer()[0].Value("");
                    formItemViewModel.SignatureUploaded(false);
               })
               .catch(() => {
                   UI.showErrorMessage([PHRASE_GENERALACTIONFAILURE]);
               });
            }
        };

        formItemViewModel.deleteSignatureAdditional = (data: any, parent: any) => {
            if (formItemViewModel.IsReadOnly()) {
                UI.showErrorMessage([Constants.PHRASE_CANNOTDELETEREADONLYIMAGE]);
            } else {
                me.deletePicture("AdditionalSignature", formItemViewModel.FormAnswer()[0].ID()).then(() => {
                    formItemViewModel.selected("");
                    formItemViewModel.FormAnswer()[0].Value("");
                    formItemViewModel.SignatureUploaded(false);
                })
                .catch(() => {
                    UI.showErrorMessage([PHRASE_GENERALACTIONFAILURE]);
                });
            }
        };

        formItemViewModel.deleteSignatureUpload = (data: any, parent: any) => {
            me.deletePicture("UploadPicture", data.selected()).then(() => {
                data.selected("");
                data.FormAnswer()[0].Value("");
                data.SignatureUploaded(false);
            })
                .catch(() => {
                    UI.showErrorMessage([PHRASE_GENERALACTIONFAILURE]);
                });
        };

        formItemViewModel.openUploadSignatureModal = (formitem: any, formItemIndex: any) => {
            me.viewModel.uploadSignature(true);
            me.viewModel.uploadedFormItemIndex(formItemIndex());
            me.container.find("div[data-id='uploadDialog']").modal("show");
            me.container.find(".k-upload-status-total").remove();
        };

        formItemViewModel.openUploadSignature = (formitem: any) => {
            me.viewModel.PictureURL(url + "/api/AppointmentPicture/UploadPicture/" + formitem.selected());
            me.container.find("div[data-id='pictureFormDialog']").modal("show");
        };

        formItemViewModel.openWindow = (formitem: any) => {
            me.viewModel.PictureURL(formitem.url() + "/api/AppointmentPicture/Signature/" + formitem.FormAnswer()[0].ID());
            me.container.find("div[data-id='pictureFormDialog']").modal("show");
        };

        formItemViewModel.openAdditionalWindow = (formitem: any) => {
            me.viewModel.PictureURL(formitem.url() + "/api/AppointmentPicture/AdditionalSignature/" + formitem.FormAnswer()[0].ID());
            me.container.find("div[data-id='pictureFormDialog']").modal("show");
        };

        // checks if need to show additional form for fields on change and pass in HasException form and index of form item
        formItemViewModel.fieldShowAdditionalForm = (field: IFieldObservable, formitem: IFormItemObservable, index: any) => {

            for (const item of me.viewModel.FormItems()) {
                item.isSelected(false);
            }

            let additionalFormID = formitem.AdditionalTemplateFormID();
            if (formitem.MultipleAdditionalForms()) {
                additionalFormID = field.TemplateFormID() ? field.TemplateFormID()! : 0;
            }

            if (field.HasExceptionForm() || (formitem.MultipleAdditionalForms() && additionalFormID > 0)) {
                formitem.isSelected(true);
                if (me.options.openAdditionalForm) {
                    me.options.openAdditionalForm(index(), true, additionalFormID, field);
                }
            }

        };

        formItemViewModel.AvailableAdditionalForm = ko.computed(() => {
            let id: number = 0;

            if (formItemViewModel.MultipleAdditionalForms()) {
                // Select the additional form id of the field whose answer is selected (if any)
                const selected = formItemViewModel.selected();
                const selectedFields = formItemViewModel.Fields().filter((field: any) => field.FieldValue() === selected);
                if (selectedFields.length > 0 && selectedFields[0].TemplateFormID() > 0) {
                    id = selectedFields[0].TemplateFormID();
                }
            } else if (formItemViewModel.AdditionalTemplateFormID()) {
                id = formItemViewModel.AdditionalTemplateFormID();
            }
            return id;
        });

        return formItemViewModel;
    }

    private getTargetValues(target: ITargetMaster): Array<ICodeModel<string>> {
        const fields: Array<ICodeModel<string>> = [];
        if (target.HasAddressFields) {
            for (const field of target.AddressFormItems) {
                if (field.Value) {
                    fields.push({
                        ID: field.FormText,
                        Name: field.Value,
                    });
                }
            }
        }
        if (target.HasCustomFields) {
            for (const field of target.FormItems) {
                if (field.Value) {
                    fields.push({
                        ID: field.FormText,
                        Name: UI.formatValueFromControlType(field.Value, field.ControlTypeID),
                    });
                }
            }
        }

        if (fields.length === 0) {
            fields.push({
                ID: "No Data, Target ID",
                Name: target.ID.toString(),
            });
        }

        return fields;
    }

    private getTargetListForControl(targets: ITargetMaster[]): ITargetForControl[] {
        const results: ITargetForControl[] = [];
        if (!targets || targets.length === 0) {
            return results;
        }

        for (const target of targets) {
            const fields: string[] = [];
            for (const value of this.getTargetValues(target)) {
                fields.push(`${value.ID}: ${value.Name}`);
            }
            results.push({ ID: target.ID, Name: fields.join(", ") });
        }

        return results;
    }

    private onUpload(e: kendo.ui.UploadUploadEvent): void {
        const me: FormDataEntryControl = this;
        const app: IApplication = Global.app();
        const url: string = app.webServiceBaseURL;
        e.sender.options.async!.saveUrl = url + "/api/FileUploadApi/Save/" + me.viewModel.FormItems()[0].AppointmentID();
    }

    private onComplete(e: kendo.ui.UploadEvent): void {
        const me: FormDataEntryControl = this;

        // call web service to get uploaded file from temp table
        if (me.viewModel.uploadedGuid() !== "") {
            AppointmentData.GetUploadedFile(me.viewModel.uploadedGuid())
                .done((response: Data.IResponse<AppointmentData.IAppointmentTempDocument>) => {

                    let fileParts: string[];
                    let extension: string = "";

                    if (response.Success) {

                        if (me.viewModel.uploadSignature()) {
                            // get the extension of the uploaded file.  The file saved in S3
                            // will have this extension so we need to make sure the extension is
                            // saved in the FormAnswer.
                            // this is very hacky, may want to find a better way to get this info
                            fileParts = response.Payload.StorageKey.split(".");
                            if (fileParts.length > 1) {
                                extension = "." + fileParts[fileParts.length - 1];
                            }

                            // Setting SignatureUploaded to true indicates that an image has been uploaded but not yet
                            // saved into the Appointment records in the DB
                            me.viewModel.FormItems()[me.viewModel.uploadedFormItemIndex()].SignatureUploaded(true);

                            if (me.viewModel.FormItems()[me.viewModel.uploadedFormItemIndex()].FormAnswer().length > 0) {
                                // If there is already an existing FormAnswer for the signature control, then update it

                                // we are casting to any, because for some reason type script doesn't think FormAnswer()[0] is an observable
                                const answer = me.viewModel.FormItems()[me.viewModel.uploadedFormItemIndex()].FormAnswer()[0] as any;
                                answer.Value(response.Payload.FileUniqueKey + extension);
                            } else {
                                // This is the first time a signature is being attached, so their is no existing FormAnswer record.
                                // So create one.
                                me.viewModel.FormItems()[me.viewModel.uploadedFormItemIndex()].FormAnswer.push(me.FormAnswers(
                                    { ID: 0, Value: response.Payload.FileUniqueKey + extension }));
                            }
                            me.viewModel.FormItems()[me.viewModel.uploadedFormItemIndex()].selected(response.Payload.ID.toString());

                            // close dialog, since you can only attach one signature image per control
                            me.viewModel.closeUploadModal();
                        } else {
                            // attaching pictures to comments
                            me.viewModel.FormItems()[me.viewModel.uploadedFormItemIndex()]
                                .Comments()[me.viewModel.uploadedCommentIndex()].Pictures.push(me.Pictures(
                                    {
                                        ID: 0, TempID: response.Payload.ID, PictureID: response.Payload.FileUniqueKey,
                                        StorageKey: response.Payload.StorageKey, IsUpload: true,
                            }));

                        }

                    } else {
                        UI.showErrorMessage(response.ErrorMessages);
                    }

                });
        }
        me.container.find(".k-upload-files").remove();
        me.viewModel.uploadedGuid("");
    }

    private onSuccess(e: kendo.ui.UploadSuccessEvent) {
        this.viewModel.uploadedGuid(e.response);
    }

    private onCompleteDocument(e: any): void {
        const me: FormDataEntryControl = this;

        // call web service to get uploaded file from temp table
        if (me.viewModel.uploadedGuid() !== "") {
            AppointmentData.GetUploadedFile(me.viewModel.uploadedGuid())
                .done((response: Data.IResponse<AppointmentData.IAppointmentTempDocument>) => {
                    if (response.Success) {
                        me.viewModel.FormItems()[me.viewModel.uploadedFormItemIndex()]
                            .Documents.push(me.Documents(
                                {
                                    ID: 0,
                                    TempID: response.Payload.ID,
                                    FileUniqueKey: response.Payload.FileUniqueKey,
                                    StorageKey: response.Payload.StorageKey,
                                    Description: "",
                                    FileName: response.Payload.FileName,
                                    IsUpload: true,
                            }));
                        if (me.viewModel.FormItems()[me.viewModel.uploadedFormItemIndex()].IsRequired()) {
                            me.viewModel.FormItems()[me.viewModel.uploadedFormItemIndex()].ItemHasValue(true);
                        }

                    } else {
                        UI.showErrorMessage(response.ErrorMessages);
                    }

                });
        }
        me.container.find(".k-upload-files").remove();
        me.viewModel.uploadedGuid("");
    }

    private Fields(fields: any): any {
        const vmField: IFieldObservable = {
            ID: ko.observable(fields ? fields.ID : 0),
            FieldText: ko.observable(fields ? fields.FieldText : ""),
            FieldValue: ko.observable(fields ? fields.FieldValue : ""),
            SortOrder: ko.observable(fields ? fields.SortOrder : 0),
            HasException: ko.observable(fields ? fields.HasException : false),
            HasExceptionForm: ko.observable(fields ? fields.HasExceptionForm : false),
            IsActive: ko.observable(fields ? fields.IsActive : true),
            TemplateFormID: ko.observable(fields ? fields.TemplateFormID : null),
            AppointmentFormItemID: ko.observable(),
        };
        return vmField;
    }

    private FormItemDocumentFileTypeAllowed(allowedFileType: AppointmentData.IAppointmentFormItemDocumentTypeAllowed): any {
        const vmDocumentFileTypeAllowed: any = {
            ID: ko.observable(allowedFileType ? allowedFileType.ID : 0),
            AppointmentID: ko.observable(allowedFileType ? allowedFileType.AppointmentID : 0),
            AppointmentFormItemID: ko.observable(allowedFileType ? allowedFileType.AppointmentFormItemID : 0),
            DocumentTypeExtension: ko.observable(allowedFileType ? allowedFileType.DocumentTypeExtension : ""),
            DocumentFileTypeAllowedID: ko.observable(allowedFileType ? allowedFileType.DocumentFileTypeAllowedID : 0),
        };
        return vmDocumentFileTypeAllowed;
    }

    private Documents(theDocument: AppointmentData.IAppointmentDocument): any {
        const me: FormDataEntryControl = this;
        const app: IApplication = Global.app();

        const vmDocuments: any = {
            ID: ko.observable(theDocument ? theDocument.ID : 0),
            FileName: ko.observable(theDocument ? theDocument.FileName : ""),
            TempID: ko.observable(theDocument ? theDocument.TempID : 0),
            Description: ko.observable(theDocument ? theDocument.Description : ""),
            FileUniqueKey: ko.observable(theDocument ? theDocument.FileUniqueKey : ""),
            PictureURL: ko.observable(""),
            StorageKey: ko.observable(theDocument ? theDocument.StorageKey : ""),
            IsUpload: ko.observable(theDocument ? theDocument.IsUpload : false),
            url: ko.observable(app.webServiceBaseURL),
        };

        vmDocuments.deleteDocument = (document: any, parent: any) => {
            // Deletes from AppointmentDocument
            UI.showConfirmDialog("Are you sure you want to delete the file \"" + document.FileName() + "\"?",
                () => {
                    me.deletePicture("Document", document.ID());
                    parent.Documents.remove(document);
                    parent.ItemHasValue(me.FormItemHasValue(parent));
                });
        };
        vmDocuments.deleteAdditionalDocument = (document: any, parent: any) => {
            // Deletes From AppointmentAdditionalDocument
            UI.showConfirmDialog("Are you sure you want to delete the file \"" + document.FileName() + "\"?",
                () => {
                    me.deletePicture("AdditionalDocument", document.ID());
                    parent.Documents.remove(document);
                    parent.ItemHasValue(me.FormItemHasValue(parent));
                });
        };
        vmDocuments.deleteUploadDocument = (document: any, parent: any) => {
            // Deletes from appointmentTempDocument
            UI.showConfirmDialog("Are you sure you want to delete the file \"" + document.FileName() + "\"?",
                () => {
                    me.deletePicture("UploadPicture", document.TempID());
                    parent.Documents.remove(document);
                    parent.ItemHasValue(me.FormItemHasValue(parent));
                });
        };
        return vmDocuments;

    }
    private Comments(comments: any): any {
        const me: FormDataEntryControl = this;
        const vmComments: any = {
            ID: ko.observable(comments ? comments.ID : 0),
            Comment: ko.observable(comments ? comments.Comment : ""),
            Pictures: ko.observableArray(),
        };

        $(comments.Pictures).each((index: number, pictures: any) => {
            vmComments.Pictures.push(me.Pictures(pictures));
        });

        vmComments.uploadImage = (comment: any, formItem: any, commentindex: any, formItemIndex: any) => {
            me.viewModel.uploadedCommentIndex(commentindex());
            me.viewModel.uploadedFormItemIndex(formItemIndex());
            me.viewModel.uploadSignature(false);
            me.container.find("div[data-id='uploadDialog']").modal("show");
            me.container.find(".k-upload-status-total").remove();
        };
        return vmComments;
    }

    private TemplatePhrases(phrases: AppointmentData.ITemplatePhraseDetail): any {
        const vmPhrases: ITemplatePhraseDetail = {
            ID: ko.observable(phrases ? phrases.ID : 0),
            Phrase: ko.observable(phrases ? phrases.Phrase : ""),
            SortOrder: ko.observable(phrases ? phrases.SortOrder : 0),
        };

        return vmPhrases;
    }

    private Pictures(thePicture: AppointmentData.IAppointmentPicture): any {
        const me: FormDataEntryControl = this;
        const app: IApplication = Global.app();
        const vmPictures: any = {
            ID: ko.observable(thePicture ? thePicture.ID : 0),
            TempID: ko.observable(thePicture ? thePicture.TempID : 0),
            PictureID: ko.observable(thePicture ? thePicture.PictureID : ""),
            StorageKey: ko.observable(thePicture ? thePicture.StorageKey : ""),
            PictureURL: ko.observable(""),
            IsUpload: ko.observable(thePicture ? thePicture.IsUpload : false),
            url: ko.observable(app.webServiceBaseURL),
        };
        vmPictures.openPicture = (picture: any, formitem: any) => {
            me.viewModel.PictureURL(picture.url() + "/api/AppointmentPicture/Picture/" + picture.ID());
            me.container.find("div[data-id='pictureFormDialog']").modal("show");
        };
        vmPictures.openAdditionalPicture = (picture: any, formitem: any) => {
            me.viewModel.PictureURL(picture.url() + "/api/AppointmentPicture/AdditionalPicture/" + picture.ID());
            me.container.find("div[data-id='pictureFormDialog']").modal("show");
        };
        vmPictures.openUploadPicture = (picture: any, formitem: any) => {
            me.viewModel.PictureURL(picture.url() + "/api/AppointmentPicture/UploadPicture/" + picture.TempID());
            me.container.find("div[data-id='pictureFormDialog']").modal("show");
        };

        vmPictures.deletePicture = (picture: any, formitem: any, parent: any) => {
            if (formitem.IsReadOnly()) {
                UI.showErrorMessage([Constants.PHRASE_CANNOTDELETEREADONLYIMAGE]);
            } else {
                 me.deletePicture("Picture", picture.ID());
                 parent.Pictures.remove(picture);
            }
        };
        vmPictures.deleteAdditionalPicture = (picture: any, formitem: any, parent: any) => {
            if (formitem.IsReadOnly()) {
                UI.showErrorMessage([Constants.PHRASE_CANNOTDELETEREADONLYIMAGE]);
            } else {
                 me.deletePicture("AdditionalPicture", picture.ID());
                 parent.Pictures.remove(picture);
            }
        };
        vmPictures.deleteUploadPicture = (picture: any, formitem: any, parent: any) => {
            me.deletePicture("UploadPicture", picture.TempID());
            parent.Pictures.remove(picture);
        };

        return vmPictures;
    }

    private deletePicture(pictureType: string, PictureID: number): Promise<any> {
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();
        return AppointmentData.DeletePictures(pictureType, PictureID)
            .done((response: Data.IResponse<boolean>) => {
                if (response.Success) {
                    deferred.resolve();
                } else {
                    UI.showErrorMessage(response.ErrorMessages);
                    deferred.reject();
                }
            })
            .fail(() => {
                deferred.reject();
            }).promise();
    }

    private AdditionalFormItems(additionalformitems: any): any {

        const vmAdditionalFormItem: any = {
            TemplateFormItemID: ko.observable(additionalformitems ? additionalformitems.TemplateFormItemID : 0),
            Answers: ko.observableArray(additionalformitems.Answers ? additionalformitems.Answers : ""),
            Comments: ko.observableArray(additionalformitems.Comments ? additionalformitems.Comments : ""),
            Documents: ko.observableArray(additionalformitems.Documents ? additionalformitems.Documents : ""),
        };

        return vmAdditionalFormItem;
    }

    private FormAnswers(formAnswers: any): any {
        const vmFormAnswer: any = {
            ID: ko.observable(formAnswers ? formAnswers.ID : 0),
            Value: ko.observable(formAnswers ? formAnswers.Value : ""),
        };

        return vmFormAnswer;
    }

    private LatLongMissing( formItem: IFormItemObservable): boolean {
        let isMissing: boolean = false;
        if (formItem.ControlTypeID() === Constants.QuestionType.GEOLOCAITON &&
            !formItem.IsReadOnly()) {
            if ( ( !formItem.Latitude() && !formItem.Longitude()) ||
                 ( formItem.Latitude() && formItem.Longitude())) {
                isMissing = false;
            } else {
            isMissing = true ;
            }
        }
        return isMissing;
    }
    private FormItemHasValue(formItem: IFormItemObservable): boolean {
        let isValid: boolean = true;
        if ((!formItem.IsReadOnly()) && formItem.IsRequired() && formItem.IsVisible()) {
            switch (formItem.ControlTypeID()) {
                case Constants.QuestionType.MULTIPLECOMMENTS:
                case Constants.QuestionType.MULTIPLECOMMENTSPICS:
                    isValid = formItem.Comments().length > 0;
                    break;
                case Constants.QuestionType.DOCUMENTS:
                    isValid = formItem.Documents().length > 0;
                    break;
                case Constants.QuestionType.GEOLOCAITON:
                    isValid = (formItem.Latitude() && formItem.Longitude()) ? isValid : false;
                    break;
            }
        }
        return isValid;
    }

    private GetTemplateName(controlType: number): string {
        if (controlType === Constants.QuestionType.TEXTBOX) {
            return "template_text-template";
        } else if (controlType === Constants.QuestionType.DROPDOWN) {
            return "template_select-template";
        } else if (controlType === Constants.QuestionType.RADIOBUTTONLIST) {
            return "template_radio-template";
        } else if (controlType === Constants.QuestionType.CHECKBOXLIST) {
            return "template_checkbox-template";
        } else if (controlType === Constants.QuestionType.DATE) {
            return "template_date-template";
        } else if (controlType === Constants.QuestionType.TEXTAREA) {
            return "template_textarea-template";
        } else if (controlType === Constants.QuestionType.NUMERIC) {
            return "template_numeric-template";
        } else if (controlType === Constants.QuestionType.SECTION) {
            return "template_section-template";
        } else if (controlType === Constants.QuestionType.TEXTDESCRIPTION) {
            return "template_textdescription-template";
        } else if (controlType === Constants.QuestionType.BUTTON) {
            return "template_button-template";
        } else if (controlType === Constants.QuestionType.SIGNATURE) {
            return "template_signature-template";
        } else if (controlType === Constants.QuestionType.MULTIPLECOMMENTS) {
            return "template_multiplecomments-template";
        } else if (controlType === Constants.QuestionType.MULTIPLECOMMENTSPICS) {
            return "template_multiplecommentspics-template";
        } else if (controlType === Constants.QuestionType.BARCODE) {
            return "template_text-template";
        } else if (controlType === Constants.QuestionType.DOCUMENTS) {
            return "template_documents-template";
        } else if (controlType === Constants.QuestionType.DATETIME) {
            return "template_datetime-template";
        } else if (controlType === Constants.QuestionType.TIME) {
            return "template_time-template";
        } else if (controlType === Constants.QuestionType.EMAIL) {
            return "template_email-template";
        } else if (controlType === Constants.QuestionType.PHONE) {
            return "template_phone-template";
        } else if (controlType === Constants.QuestionType.LINKEDDOCUMENTS) {
            return "template_linkeddocuments-template";
        } else if (controlType === Constants.QuestionType.TARGET) {
            return "template_target-template";
        } else if (controlType === Constants.QuestionType.ADDRESS) {
            return "template_address-template";
        } else if (controlType === Constants.QuestionType.GEOLOCAITON) {
            return "template_geolocation-template";
        } else if (controlType === Constants.QuestionType.SLIDER) {
            return "template_slider-template";
        } else if ( controlType === Constants.QuestionType.SINGLECHECKBOX) {
            return "template_singlecheckbox-template";
        } else {
            return "";
        }
    }

    // Converts the standard date/time formats into the formats used by the customer
    private ConvertDateTimeFormatsForFormItems(formItems: AppointmentData.IAppointmentFormItem[]) {
        const timeFormat: string = UI.getMomentTimeFormat(this.viewModel.TimeFormat);
        const dateFormat: string = UI.getMomentDateFormat(this.viewModel.DateFormat);

        for (const formItem of formItems) {
            if (formItem.FormAnswer && formItem.FormAnswer.length > 0 && formItem.FormAnswer[0].Value) {
                const currentDate = formItem.FormAnswer[0].Value;

                switch (formItem.ControlTypeID) {
                    case Constants.QuestionType.TIME:
                        formItem.FormAnswer[0].Value = moment(currentDate, Constants.MOMENT_FORMAT_TIME_STANDARDIZED).format(timeFormat);
                        break;
                    case Constants.QuestionType.DATE:
                        formItem.FormAnswer[0].Value = moment(currentDate, Constants.MOMENT_FORMAT_DATE_STANDARDIZED).format(dateFormat);
                        break;
                    case Constants.QuestionType.DATETIME:
                        formItem.FormAnswer[0].Value = moment(currentDate).local().format(`${dateFormat} ${timeFormat}`);
                        break;
                }
            }

        }
    }
    private answerChecked(answer: string): boolean {
        return answer === Constants.SINGLECHECKBOX_ANSWER_TRUE;
    }

    private getMaxUploadFileSizeMessage(maxFileSizeInBytes: number): string {
        const BYTES_PER_KB = 1000;
        const BYTES_PER_MB = 1000000;
        if (maxFileSizeInBytes >= BYTES_PER_MB) {
            return `Maximum file size is ${Math.round(maxFileSizeInBytes / BYTES_PER_KB)} KB/${Math.round(maxFileSizeInBytes / BYTES_PER_MB)} MB.`;
        } else if (maxFileSizeInBytes >= BYTES_PER_KB ) {
            return `Maximum file size is ${Math.round(maxFileSizeInBytes / BYTES_PER_KB)} KB.`;
        } else if (maxFileSizeInBytes > 0) {
            return `Maximum file size is ${maxFileSizeInBytes} bytes.`;
        } else {
            return "";
        }

    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--7!./src/script/common/formDataEntry.ts